import { AnimatedSection } from "@/components/common/AnimatedSection"
import { SectionLabel } from "@/components/common/SectionLabel"
import { Button } from "@/components/ui/Button"
import { Card } from "@/components/ui/Card"
import { useSEO } from "@/hooks/useSEO"
import { CTASection } from "@/sections/CTASection"
import { createFileRoute, Link } from "@tanstack/react-router"
import { motion } from "framer-motion"
import {
  Activity,
  ArrowRight,
  CheckCircle2,
  Lock,
  RefreshCw,
  Shield,
  Table2,
  Zap,
} from "lucide-react"

export const Route = createFileRoute("/features/gateway")({
  component: GatewayPage,
})

const GATEWAY_CAPABILITIES = [
  {
    icon: Shield,
    color: "bg-blue-light",
    iconColor: "text-blue",
    title: "Fraud Prevention",
    description:
      "3D Secure 2.0, ML-powered risk scoring, and real-time transaction monitoring protect every payment before it settles.",
    bullets: [
      "3D Secure 2.0 authentication",
      "ML risk scoring engine",
      "Device fingerprinting",
      "Chargeback dispute tools",
    ],
  },
  {
    icon: Table2,
    color: "bg-amber-light",
    iconColor: "text-amber-DEFAULT",
    title: "Dynamic Descriptors",
    description:
      "Customize the statement descriptor per transaction and merchant, dramatically reducing confusion-driven chargebacks.",
    bullets: [
      "Per-transaction descriptors",
      "Merchant-level overrides",
      "Soft & hard descriptors",
      "Chargeback reduction toolkit",
    ],
  },
  {
    icon: Activity,
    color: "bg-green-light",
    iconColor: "text-green-DEFAULT",
    title: "Velocity Controls",
    description:
      "Configurable rate-limiting at the card, BIN, IP, and merchant level. Defaults tuned for safety, overridable per business.",
    bullets: [
      "Card-level velocity rules",
      "BIN range controls",
      "IP & device throttling",
      "Custom exception lists",
    ],
  },
  {
    icon: Zap,
    color: "bg-rose-light",
    iconColor: "text-rose-DEFAULT",
    title: "Smart Routing",
    description:
      "Route transactions dynamically to the best-fit acquirer based on cost, approval rate, currency, and geography.",
    bullets: [
      "Multi-acquirer failover",
      "Cost-optimized routing",
      "Approval rate targeting",
      "Country-aware paths",
    ],
  },
  {
    icon: RefreshCw,
    color: "bg-blue-light",
    iconColor: "text-blue",
    title: "Retry Logic",
    description:
      "Automatic smart retries on soft declines using updated network tokens and alternative routing to maximise revenue.",
    bullets: [
      "Network tokenisation",
      "Intelligent retry rules",
      "Decline code analysis",
      "Recovery rate reporting",
    ],
  },
  {
    icon: Lock,
    color: "bg-green-light",
    iconColor: "text-green-DEFAULT",
    title: "Tokenisation",
    description:
      "Store and reuse card credentials safely with PCI-compliant tokens. Supports network tokens and proprietary vaulting.",
    bullets: [
      "PCI DSS Level 1 vault",
      "Network token support",
      "Multi-merchant tokens",
      "One-click checkout ready",
    ],
  },
]

const PERFORMANCE_STATS = [
  { value: "98.9%", label: "Approval rate", color: "text-green-DEFAULT" },
  { value: "<200ms", label: "API latency (p99)", color: "text-blue" },
  { value: "0.03%", label: "Fraud rate", color: "text-ink" },
  { value: "99.99%", label: "Gateway uptime", color: "text-ink" },
]

const SUPPORTED_METHODS = [
  { category: "Cards", items: ["Visa", "Mastercard", "Amex", "Discover", "JCB", "UnionPay"] },
  {
    category: "Bank Transfers",
    items: ["SEPA Credit", "SEPA Direct Debit", "ACH", "BACS", "SWIFT"],
  },
  { category: "Digital Wallets", items: ["Apple Pay", "Google Pay", "PayPal", "Click to Pay"] },
  {
    category: "Local Methods",
    items: ["iDEAL", "Bancontact", "Sofort", "Klarna", "Boleto", "PIX"],
  },
]

const INTEGRATION_STEPS = [
  {
    step: "01",
    title: "Get your API keys",
    description:
      "Sign up, verify your business, and retrieve sandbox + live credentials from the dashboard.",
  },
  {
    step: "02",
    title: "Install the SDK",
    description:
      "npm install @orchestrate/node — or use any HTTP client with our REST API directly.",
  },
  {
    step: "03",
    title: "Create a payment",
    description:
      "POST /v1/payments with amount, currency, and payment method. Receive a payment object back instantly.",
  },
  {
    step: "04",
    title: "Handle webhooks",
    description:
      "Subscribe to payment.completed, payment.failed, and chargeback.created for real-time updates.",
  },
]

function GatewayPage() {
  useSEO({
    title: "Payment Gateway",
    description:
      "Enterprise payment gateway with ML fraud prevention, 3D Secure 2.0, dynamic descriptors, smart routing, and intelligent velocity controls.",
    keywords:
      "payment gateway, fraud prevention, 3D Secure, dynamic descriptors, velocity controls, payment tokenisation",
    canonical: "https://orchestrate.global/gateway",
  })

  return (
    <>
      {/* Hero */}
      <section className="bg-surface grid-bg relative overflow-hidden pt-32 pb-20">
        <div className="hero-glow" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div>
              <AnimatedSection>
                <SectionLabel label="Payment Gateway" className="mb-5" />
              </AnimatedSection>
              <AnimatedSection delay={0.1}>
                <h1 className="font-display mb-6 text-5xl leading-tight tracking-tight sm:text-6xl lg:text-7xl">
                  Enterprise-grade
                  <br />
                  controls, <em className="text-blue not-italic">zero overhead.</em>
                </h1>
              </AnimatedSection>
              <AnimatedSection delay={0.2}>
                <p className="text-muted mb-10 max-w-lg text-xl leading-relaxed">
                  A payment gateway built for high-volume, complex businesses. Advanced fraud
                  prevention, smart routing, and complete configurability — all in a single API
                  call.
                </p>
              </AnimatedSection>
              <AnimatedSection delay={0.3}>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary" size="lg" asChild>
                    <Link to="/contact">
                      Start integrating <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/docs">View API docs</Link>
                  </Button>
                </div>
              </AnimatedSection>
            </div>

            <AnimatedSection direction="right" delay={0.2}>
              <div className="border-border shadow-ink/5 rounded-2xl border bg-white p-8 shadow-xl">
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-base font-semibold">Gateway Performance</h2>
                  <div className="bg-green-light border-green-DEFAULT/20 text-green-DEFAULT flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold">
                    <span
                      className="bg-green-DEFAULT animate-pulse-dot h-1.5 w-1.5 rounded-full"
                      aria-hidden="true"
                    />
                    Live
                  </div>
                </div>
                <div className="mb-6 grid grid-cols-2 gap-4">
                  {PERFORMANCE_STATS.map(({ value, label, color }) => (
                    <div key={label} className="bg-surface border-border rounded-xl border p-5">
                      <p className={`font-display mb-1 text-3xl tracking-tight ${color}`}>
                        {value}
                      </p>
                      <p className="text-muted text-xs">{label}</p>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-muted mb-2 flex items-center justify-between text-xs">
                    <span>Approval rate (30 days)</span>
                    <span className="text-green-DEFAULT font-semibold">↑ 1.2%</span>
                  </div>
                  <div
                    className="flex h-12 items-end gap-1"
                    role="img"
                    aria-label="Approval rate trend chart"
                  >
                    {[72, 85, 78, 90, 83, 92, 88, 95, 91, 97, 93, 98].map((h, i) => (
                      <motion.div
                        key={i}
                        className={`flex-1 rounded-t ${i === 11 ? "bg-blue" : "bg-blue-mid"}`}
                        style={{
                          height: `${h}%`,
                          transformOrigin: "bottom", // Ensures scaling happens from the bottom
                        }}
                        initial={{ scaleY: 0 }}
                        whileInView={{ scaleY: 1 }}
                        transition={{
                          scaleY: {
                            duration: 0.6,
                            delay: i * 0.04,
                            ease: [0.16, 1, 0.3, 1],
                          },
                        }}
                        viewport={{ once: true }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Capabilities grid */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <AnimatedSection>
              <SectionLabel label="Capabilities" centered className="mb-5" />
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <h2 className="font-display text-4xl tracking-tight sm:text-5xl">
                Every tool to process <em className="text-blue not-italic">with confidence.</em>
              </h2>
            </AnimatedSection>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {GATEWAY_CAPABILITIES.map((cap, i) => {
              const Icon = cap.icon
              return (
                <motion.div
                  key={cap.title}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  viewport={{ once: true, amount: 0.08 }}
                >
                  <Card lift className="h-full p-6">
                    <div
                      className={`h-11 w-11 ${cap.color} mb-5 flex items-center justify-center rounded-xl`}
                    >
                      <Icon className={`h-5 w-5 ${cap.iconColor}`} />
                    </div>
                    <h3 className="mb-2 text-base font-semibold">{cap.title}</h3>
                    <p className="text-muted mb-4 text-sm leading-relaxed">{cap.description}</p>
                    <ul className="space-y-1.5">
                      {cap.bullets.map((b) => (
                        <li key={b} className="text-muted flex items-center gap-2 text-xs">
                          <CheckCircle2 className="text-green-DEFAULT h-3.5 w-3.5 flex-shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </Card>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Supported payment methods */}
      <section className="bg-surface py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <AnimatedSection>
              <SectionLabel label="Payment Methods" centered className="mb-5" />
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <h2 className="font-display text-4xl tracking-tight sm:text-5xl">
                Every method your customers <em className="text-blue not-italic">want to use.</em>
              </h2>
            </AnimatedSection>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {SUPPORTED_METHODS.map((group, i) => (
              <AnimatedSection key={group.category} delay={i * 0.1}>
                <div className="border-border h-full rounded-2xl border bg-white p-6">
                  <h3 className="text-muted mb-4 text-xs font-semibold tracking-widest uppercase">
                    {group.category}
                  </h3>
                  <ul className="space-y-2">
                    {group.items.map((item) => (
                      <li key={item} className="flex items-center gap-2.5 text-sm font-medium">
                        <div
                          className="bg-blue h-1.5 w-1.5 flex-shrink-0 rounded-full"
                          aria-hidden="true"
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Integration steps */}
      <section className="bg-ink relative overflow-hidden py-24">
        <div
          className="grid-bg pointer-events-none absolute inset-0 opacity-20"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mb-16 text-center">
            <AnimatedSection>
              <SectionLabel label="Quick Start" centered className="mb-5" />
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <h2 className="font-display text-4xl tracking-tight text-white sm:text-5xl">
                Integrate in <em className="text-blue-mid not-italic">under an hour.</em>
              </h2>
            </AnimatedSection>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {INTEGRATION_STEPS.map((step, i) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ once: true, amount: 0.08 }}
              >
                <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-6 transition-colors hover:bg-white/8">
                  <span className="font-display mb-4 block text-5xl leading-none text-white/10">
                    {step.step}
                  </span>
                  <h3 className="mb-2 text-base font-semibold text-white">{step.title}</h3>
                  <p className="text-sm leading-relaxed text-white/50">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <AnimatedSection delay={0.4}>
            <div className="mt-12 text-center">
              <Button variant="primary" size="lg" asChild>
                <Link to="/docs">
                  Read the full docs <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <CTASection />
    </>
  )
}
